/**
 * CompareChart.jsx — Superposition de plusieurs sessions sur un axe commun
 *
 * Deux alignements (alignMode, calculs dans compareAxis.js) :
 *   - "fromStart" : minutes depuis le début de chaque session (0, 1, 2… durée),
 *     courbes rééchantillonnées à la minute (défaut, comportement historique).
 *   - "clock"     : heure réelle de chaque mesure (axe linéaire en ms), toutes
 *     les sessions sur le même axe ; graduations « HH:mm » sur un seul jour,
 *     « JJ/MM HH:mm » sinon. Mention discrète si les sessions ne se chevauchent pas.
 *
 * Une couleur par session (comparePalette.js), légende dynamique. lineStyles
 * (D257) : 'solid' par défaut, trait plein à l'écran ; 'dashed' pour les
 * captures PDF (captureCompareChart), un motif par rang. Lignes de seuil et zoom dans les deux modes ; les seuils sont
 * dessinés au-dessus des courbes (THRESHOLD_ORDER) et hors infobulle. Le zoom est
 * réinitialisé au changement d'alignement. devicePixelRatio (optionnel) force
 * la résolution du canvas pour la capture PDF (captureCompareChart), chartHeight
 * (optionnel, 300 px par défaut) sa hauteur.
 *
 * Plage d'analyse (10.5.297), props optionnelles et inactives par défaut (le
 * PDF clinique vétérinaire capture ce composant sans elles) :
 *   - analysisWindow { min, max } en unités de l'axe (ms en heure réelle,
 *     minutes depuis le début sinon) : vue cadrée sur la plage, zones hors
 *     plage grisées quand on dézoome au-delà ;
 *   - onAnalyzeZoom(from, to) : heure réelle seulement, bouton « Analyser la
 *     vue zoomée » visible après un zoom ou un déplacement, plage en ms epoch.
 *
 * Zoom synchronisé (onglet « Tous les paramètres », CompareAllCharts) :
 * onViewChange({ min, max } | null) après un zoom, un déplacement ou une
 * réinitialisation ; view impose la vue d'un autre graphique, sans animation.
 * Options et données sont mémoïsées : react-chartjs-2 recopie sur le graphique
 * tout objet nouveau (Object.assign puis update), ce qui remet l'axe X sur la
 * fenêtre d'analyse et annule le zoom ; un rendu du parent (vue propagée,
 * survol de la légende) ne recrée donc rien à paramètres égaux (10.5.336).
 *
 * Au-delà de DECIMATION_MIN_POINTS mesures dans une série, en heure réelle :
 * données non reparsées (parsing: false) et décimation LTTB, pour que la pile
 * de graphiques reste fluide (jusqu'à 6 × 4 × 10 000 points).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Chart as ChartJS, CategoryScale, LinearScale,
  PointElement, LineElement, Title, Tooltip, Legend, Filler, Decimation,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import zoomPlugin from 'chartjs-plugin-zoom';
import annotationPlugin from 'chartjs-plugin-annotation';
import {
  ALIGN_MODES, buildFromStartSeries, buildClockSeries, clockBounds,
  sessionsOverlap, isSingleDay,
} from './compareAxis';
import { LINE_STYLES } from '../constants/comparePalette';
import { buildCompareChartConfig } from './compareChartConfig';
import { findMetric, metricDecimals } from '../sessions/compareMetrics';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement,
  Title, Tooltip, Legend, Filler, Decimation, zoomPlugin, annotationPlugin);

export { THRESHOLD_ORDER } from './compareChartConfig';

export const DECIMATION_MIN_POINTS = 5000;

const CompareChart = ({ sessions, field = 'pm2_5', yLabel = 'PM2.5 (µg/m³)',
                        thresholds = [], alignMode = ALIGN_MODES.FROM_START, darkMode = false,
                        devicePixelRatio, chartHeight = 300, analysisWindow = null, onAnalyzeZoom,
                        lineStyles = LINE_STYLES.SOLID, view = null, onViewChange }) => {
  const { t } = useTranslation();
  const chartRef = useRef(null);
  const isClock = alignMode === ALIGN_MODES.CLOCK;
  // Décimales de l'infobulle selon la mesure (ppm entiers pour le CO₂).
  const decimals = metricDecimals(findMetric(field));
  // Vue zoomée ou déplacée par l'utilisateur : propose de l'analyser.
  const [zoomed, setZoomed] = useState(false);
  const onViewChangeRef = useRef(onViewChange);
  onViewChangeRef.current = onViewChange;
  // Stable : cité par les options mémoïsées.
  const markZoomed = useCallback((ctx) => {
    setZoomed(true);
    const x = ctx?.chart?.scales?.x;
    if (onViewChangeRef.current && x) onViewChangeRef.current({ min: x.min, max: x.max });
  }, []);

  // Zoom réinitialisé au changement d'alignement (les abscisses changent de
  // nature), jamais au montage : rien à réinitialiser.
  const previousAlignMode = useRef(alignMode);
  useEffect(() => {
    if (previousAlignMode.current === alignMode) return;
    previousAlignMode.current = alignMode;
    chartRef.current?.resetZoom();
  }, [alignMode]);
  // Nouvelle plage : le graphique est remonté (key), la vue repart cadrée.
  const windowKey = analysisWindow ? `${analysisWindow.min}:${analysisWindow.max}` : 'full';
  useEffect(() => { setZoomed(false); }, [windowKey, alignMode]);

  // Vue imposée par un graphique voisin ; null après sa réinitialisation.
  const viewKey = view ? `${view.min}:${view.max}` : null;
  const previousViewKey = useRef(viewKey);
  useEffect(() => {
    if (previousViewKey.current === viewKey) return;
    previousViewKey.current = viewKey;
    const chart = chartRef.current;
    if (!chart) return;
    if (view) chart.zoomScale('x', { min: view.min, max: view.max }, 'none');
    else chart.resetZoom('none');
    setZoomed(Boolean(view));
    // view est lu par sa clé : un nouvel objet de même bornes ne rezoome pas.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewKey]);

  const c = useMemo(() => ({
    text:    darkMode ? '#E1E1E1' : '#2C3E50',
    grid:    darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
    tooltip: darkMode ? '#1E1E32' : '#fff',
  }), [darkMode]);

  // Calcul des données — mémoïsé pour éviter le recalcul à chaque render
  const { labels, seriesData, bounds } = useMemo(() => {
    if (!sessions.length) return { labels: [], seriesData: [], bounds: null };
    if (isClock) {
      return { labels: undefined, seriesData: buildClockSeries(sessions, field), bounds: clockBounds(sessions) };
    }
    const { labels: minutes, series } = buildFromStartSeries(sessions, field);
    return { labels: minutes, seriesData: series, bounds: null };
  }, [sessions, field, isClock]);

  const decimate = isClock && seriesData.some(points => points.length > DECIMATION_MIN_POINTS);
  const singleDay = isSingleDay(bounds);
  const overlap = useMemo(() => sessionsOverlap(sessions), [sessions]);
  // Plage lue par ses bornes : le parent en recrée l'objet à chaque rendu.
  const winMin = analysisWindow?.min;
  const winMax = analysisWindow?.max;

  const { data, options } = useMemo(() => buildCompareChartConfig({
    sessions, labels, seriesData, bounds, isClock, decimate, singleDay, thresholds, yLabel, darkMode, c,
    lineStyles, devicePixelRatio, decimals, markZoomed, t,
    analysisWindow: winMin === undefined ? null : { min: winMin, max: winMax },
  }), [sessions, labels, seriesData, bounds, isClock, decimate, singleDay, winMin, winMax, thresholds, yLabel,
    darkMode, c, lineStyles, devicePixelRatio, decimals, markZoomed, t]);

  if (!sessions.length) return null;

  // Bornes de la vue zoomée courante (ms epoch en heure réelle).
  const analyzeZoom = () => {
    const x = chartRef.current?.scales?.x;
    if (x && Number.isFinite(x.min) && Number.isFinite(x.max) && x.min < x.max) onAnalyzeZoom(x.min, x.max);
  };

  return (
    <div style={{ position: 'relative' }}>
      <div style={{ position: 'absolute', top: 0, right: 0, display: 'flex', gap: '6px' }}>
        {isClock && onAnalyzeZoom && zoomed && (
          <button data-testid="analyze-zoom" onClick={analyzeZoom}
            style={{ padding: '4px 10px', fontSize: '11px', borderRadius: '6px',
              background: 'transparent', color: '#1B77F1', border: '1px solid #1B77F1', cursor: 'pointer' }}>
            {t('sessions.compare.range.analyzeZoom')}
          </button>
        )}
        <button onClick={() => { chartRef.current?.resetZoom(); setZoomed(false); onViewChange?.(null); }}
          style={{ padding: '4px 10px', fontSize: '11px', borderRadius: '6px',
            background: '#1B77F1', color: '#fff', border: 'none', cursor: 'pointer' }}>
          {t('common.zoom.reset')}
        </button>
      </div>
      <div style={{ height: `${chartHeight}px`, marginTop: '8px' }}>
        <Line key={`${alignMode}:${windowKey}`} ref={chartRef} data={data} options={options} />
      </div>
      {isClock && !overlap && (
        <p data-testid="compare-no-overlap"
          style={{ fontSize: '11px', color: c.text, opacity: 0.7, margin: '8px 0 0' }}>
          {t('sessions.compare.noOverlap')}
        </p>
      )}
    </div>
  );
};

export default CompareChart;
